import React, { useState, useEffect, useRef } from 'react';
import { dataService } from '../services/dataService';
import { PdfService } from '../services/PdfService';
import SummaryRenderer from './steps/SummaryRenderer';

// Import new modular components
import LMRAChecklist from './execution/LMRAChecklist';
import LOTOSection from './execution/LOTOSection';
import MechanicChecklist from './execution/MechanicChecklist';
import CompletionPanel from './execution/CompletionPanel';

import { usePermitLogic } from '../hooks/usePermitLogic';
// import localData removed - using async fetch

export default function ExecutionView({
    permit,
    onClose,
    currentUser,
    previewMode = false,
    embedded = false,
    users = [],
    allowAssignment = false,
    allowUpload = true,
    initialTab,
    config
}) {

    // Refs for scroll handling
    const completionPanelRef = useRef(null);
    const scrollContainerRef = useRef(null);

    // --- USE CUSTOM HOOK ---
    const {
        checklist,
        saving,
        status,
        conditionScore, setConditionScore,
        isSynced,
        isOnline,
        showCompletionModal, setShowCompletionModal,
        completionFeedback, setCompletionFeedback,
        instructionAnswer, setInstructionAnswer,
        instructionFeedback, setInstructionFeedback,
        completionChecks, setCompletionChecks,
        canComplete,
        progress,
        // Session Management
        executionSessionUrls,
        addExecutionSessionUrl,
        cleanupExecutionSession,
        // Actions
        handleChecklistUpdate,
        handleLotoDeviation,
        handleStatusUpdate,
        handleFinalCompletion,
        isReplacementConfirmed,
        setIsReplacementConfirmed,
        canCompleteReason
    } = usePermitLogic(permit, onClose, previewMode);

    const [activeTab, setActiveTab] = useState(initialTab || (previewMode ? 'details' : 'execution'));
    const [isoLibrary, setIsoLibrary] = useState({}); // Default empty

    const lotoSteps = permit.lotoSteps || [];
    const lotoComplete = lotoSteps.length === 0 || lotoSteps.every((_, idx) => checklist[`loto_${idx}`]);
    const allMechanicChecks = (permit.checks || []).every((_, idx) => checklist[`mechanic_check_${idx}`]);
    const allChecksDone = checklist.lmraLocked === false && lotoComplete && allMechanicChecks;

    // Manage expanded state of sections to allow re-opening and closing
    const [expandedSections, setExpandedSections] = useState({
        lmra: true,
        safety: false,
        checks: false
    });

    const toggleSection = (id) => {
        setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Auto-expand logic for guided flow
    useEffect(() => {
        // Only auto-expand safety if it's NOT a LOTO-required task.
        // If LOTO is required, we want them to see the Red Gate first.
        if (checklist.lmraLocked === false && !expandedSections.safety && !expandedSections.checks && !permit.lotoRequired) {
            setExpandedSections(prev => ({ ...prev, lmra: false, safety: true }));
        }
    }, [checklist.lmraLocked]);

    useEffect(() => {
        if (lotoComplete && checklist.lmraLocked === false && !expandedSections.checks) {
            setExpandedSections(prev => ({ ...prev, safety: false, checks: true }));
        }
    }, [lotoComplete, checklist.lmraLocked]);

    // Scroll to completion panel when it opens
    useEffect(() => {
        if (showCompletionModal && completionPanelRef.current && scrollContainerRef.current) {
            // Small delay to let component render
            // Wait long enough for collapse animations to finish
            setTimeout(() => {
                const panel = completionPanelRef.current;
                if (!panel) return;

                // Native scrollIntoView respects scroll-margin-top
                panel.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }, 600);
        }
    }, [showCompletionModal]);

    // Fetch latest library (Try DB first, then fallback to public JSON)
    React.useEffect(() => {
        const fetchLib = async () => {
            try {
                const lib = await dataService.getGlobalLibrary();
                if (lib?.iso_14224_library && Object.keys(lib.iso_14224_library).length > 0) {
                    setIsoLibrary(lib.iso_14224_library);
                } else {
                    // Fallback to Public JSON if DB is empty
                    const res = await fetch('/data/raconworks_data.json');
                    const data = await res.json();
                    if (data.iso_14224_library) {
                        setIsoLibrary(data.iso_14224_library);
                    }
                }
            } catch (e) {
                console.warn("ISO Library fetch failed", e);
            }
        };
        fetchLib();
    }, []);

    // Determine permissions
    // In previewMode, we allow interaction so the user can test the flow, 
    // but we prevent actual DB writes via usePermitLogic internal checks or status-based blocking.
    // However, we want the UI to BEHAVE as if it's active.
    const isReadOnly = (status === 'completed' || status === 'rejected') && !previewMode;
    const canAssign = (!previewMode || allowAssignment) && status !== 'completed';
    const canUpload = allowUpload; // Always allow local state upload in preview or active

    // Premium Button & Tab Styles
    const btnBase = "px-8 py-4 md:py-3 rounded-xl font-black transition-all shadow-lg flex items-center justify-center gap-2 active:scale-95 text-base md:text-sm uppercase tracking-widest shrink-0 border-2";
    const styleSuccess = { background: '#facc15', color: '#000000', borderColor: 'rgba(250, 204, 21, 0.5)', boxShadow: '0 8px 25px rgba(250, 204, 21, 0.2)' };
    const styleSecondary = { background: 'rgba(30, 41, 59, 0.8)', color: '#94a3b8', borderColor: '#334155' };

    const btnTab = (active) => `btn btn-sm flex-1 font-black uppercase tracking-[0.1em] transition-all ${active
        ? 'bg-white text-slate-900 shadow-lg scale-105'
        : 'bg-transparent text-slate-500 hover:text-white hover:bg-slate-800/50 border-none'
        }`;

    const defaultConfig = {
        spacing: 'compact',
        sections: [
            { id: 'opdracht', label: 'Opdracht', visible: true },
            { id: 'locatie', label: 'Locatie', visible: true },
            { id: 'taak', label: 'Taak', visible: true },
            { id: 'personeel', label: 'Personeel', visible: true },
            { id: 'pbms', label: "PBM's", visible: true },
            { id: 'gereedschap', label: 'Gereedschap', visible: true },
            { id: 'vergunningen', label: 'Vergunningen', visible: true },
            { id: 'loto', label: 'LOTO', visible: true },
            { id: 'risicos', label: "Risico's", visible: true },
            { id: 'werkstappen', label: 'Werkstappen', visible: true },
            { id: 'handtekeningen', label: 'Handtekeningen', visible: true }
        ]
    };

    useEffect(() => {
        return () => console.log("💀 [ExecutionView] UNMOUNTED");
    }, []);

    const activeConfig = config || defaultConfig;

    // --- LOTO SAFETY GATE LOGIC ---
    // If the task metadata says LOTO is required, and LOTO is not finished, 
    // we block the view with a high-impact overlay.
    const isLotoRequiredTask = permit.lotoRequired === true;
    const showSafetyGate = isLotoRequiredTask && !lotoComplete;

    return (
        <div ref={scrollContainerRef} className={`${embedded ? 'relative h-full border border-slate-700 rounded-lg shadow-inner' : 'fixed inset-0 z-[99999]'} bg-slate-900 overflow-y-auto flex flex-col`} style={!embedded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999, minHeight: '100vh' } : {}}>


            {/* Header */}
            <div className={`sticky top-0 bg-slate-900 border-b border-slate-800 z-20 shadow-lg ${previewMode ? 'border-t-4 border-t-yellow-500' : ''}`}>

                {previewMode && (
                    <div className="bg-yellow-500/10 text-yellow-400 text-[10px] text-center py-1 font-bold tracking-widest uppercase border-b border-yellow-500/20 flex justify-between px-4">
                        <span>⚠️ Voorbeeldweergave (Wijzigingen worden niet opgeslagen)</span>
                        <span className="opacity-50">v11:382</span>
                    </div>
                )}

                <div className="px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                        {/* LEFT: Info Column */}
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-3">
                                {!embedded && (
                                    <button onClick={async () => {
                                        // Cleanup orphan session photos before closing
                                        if (status !== 'completed') {
                                            await cleanupExecutionSession();
                                        }
                                        onClose();
                                    }} className="text-slate-500 hover:text-white transition-colors" title="Sluiten">
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                        </svg>
                                    </button>
                                )}
                                <div className="min-w-0">
                                    <div className="flex items-center gap-3 mb-1">
                                        <h2 className="text-xl font-black text-white leading-none tracking-tight">
                                            {permit.ultimoNr || 'P-CONCEPT'}
                                        </h2>
                                        <div className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-[0.15em] shadow-lg border ${status === 'completed'
                                            ? 'bg-green-500/10 text-green-500 border-green-500/30'
                                            : 'bg-blue-600/10 text-blue-400 border-blue-500/30'
                                            }`}>
                                            {status === 'completed' ? 'GEREED' : 'IN BEHANDELING'}
                                        </div>
                                    </div>
                                    <div className="text-[11px] text-slate-500 font-black uppercase tracking-widest truncate max-w-md opacity-80">
                                        {permit.description || 'Geen omschrijving'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: Status & Sync */}
                        <div className="hidden md:flex flex-col items-end gap-1">
                            {!previewMode && (
                                <div className="flex items-center gap-3">
                                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[10px] font-black transition-all ${!isOnline
                                        ? 'bg-orange-500/10 text-orange-400 border-orange-500/20'
                                        : !isSynced
                                            ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                            : 'bg-green-500/10 text-green-400 border-green-500/20'
                                        }`}>
                                        <span className={`h-1.5 w-1.5 rounded-full ${!isOnline ? 'bg-orange-400' : !isSynced ? 'bg-blue-400 animate-pulse' : 'bg-green-400'
                                            }`} />
                                        {!isOnline ? 'OFFLINE' : !isSynced ? 'SYNC...' : 'CLOUD SYNCED'}
                                    </div>
                                    <div className="text-xs font-black text-slate-600 bg-slate-800 px-2 py-1 rounded">
                                        {progress}%
                                    </div>
                                </div>
                            )}
                            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                                {permit.location} {permit.hall ? ` › ${permit.hall.split('>').pop()}` : ''}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="px-4 pb-3">
                    <div className="flex bg-slate-800/80 backdrop-blur-sm p-1 rounded-lg border border-slate-700/50">
                        <button onClick={() => setActiveTab('execution')} className={btnTab(activeTab === 'execution')}>
                            <span className="flex items-center justify-center gap-2">
                                <span className={`w-1.5 h-1.5 rounded-full ${activeTab === 'execution' ? 'bg-blue-500 animate-pulse' : 'bg-slate-600'}`} />
                                UITVOERING
                            </span>
                        </button>
                        <button onClick={() => setActiveTab('details')} className={btnTab(activeTab === 'details')}>
                            <span className="flex items-center justify-center gap-2">
                                <span className={`w-1.5 h-1.5 rounded-full ${activeTab === 'details' ? 'bg-blue-500' : 'bg-slate-600'}`} />
                                INFO / DOCUMENTATIE
                            </span>
                        </button>
                    </div>
                </div>

                {/* Technicus Assignment - Now UNDER the tabs for mobile */}
                {canAssign && (
                    <div className="px-4 py-2 bg-slate-800/50 border-t border-slate-700/50">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <label className="text-[10px] text-slate-500 uppercase font-black tracking-widest whitespace-nowrap">Technicus:</label>
                            {users && users.length > 0 ? (
                                <select
                                    className="bg-slate-900 text-white text-sm font-bold py-2 px-3 rounded-lg border border-slate-600 w-full sm:w-auto sm:min-w-[200px] focus:outline-none focus:border-blue-500"
                                    value={permit.assignedTo || ''}
                                    onChange={async (e) => {
                                        const userId = e.target.value;
                                        const user = users.find(u => u.uid === userId);
                                        if (user) {
                                            if (confirm(`Opdracht toewijzen aan ${user.username || user.name}?`)) {
                                                try {
                                                    await dataService.updatePermitAssignment(permit.id, user.uid, user.name || user.username, user.email);
                                                    alert(`Toegewezen aan ${user.name}`);
                                                    onClose();
                                                } catch (err) {
                                                    alert("Fout: " + err.message);
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <option value="">-- Kies technicus --</option>
                                    {users
                                        .filter(u => {
                                            if (!permit.location) return true;
                                            if (!u.allowedLocations || u.allowedLocations.length === 0) return false;
                                            return u.allowedLocations.includes(permit.location);
                                        })
                                        .map(u => (
                                            <option key={u.uid} value={u.uid} className="bg-slate-900">{u.username || u.name}</option>
                                        ))}
                                </select>
                            ) : (
                                <span className="text-sm text-white font-bold py-2">{permit.executor || 'Demo User'}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="flex-1 bg-slate-900 p-4 md:p-8">
                <div className="max-w-4xl mx-auto pb-20">

                    {/* TAB 1: EXECUTION */}
                    {activeTab === 'execution' && (
                        <div className="space-y-6 fade-in">
                            <LMRAChecklist
                                permit={permit}
                                checklist={checklist}
                                onUpdate={handleChecklistUpdate}
                                isReadOnly={isReadOnly}
                                previewMode={previewMode}
                                isOpen={expandedSections.lmra}
                                onToggle={() => toggleSection('lmra')}
                            />

                            {/* MAIN CONTENT - ONLY VISIBLE IF LMRA IS UNLOCKED */}
                            {checklist.lmraLocked === false && (
                                <div className="transition-all duration-500 md:animate-slide-up">

                                    {/* HIGH IMPACT SAFETY GATE (Inline) */}
                                    {showSafetyGate && (
                                        <div className="w-full bg-slate-900 border-2 border-red-600/50 rounded-2xl p-8 text-center animate-fade-in no-print shadow-2xl shadow-red-900/40 mb-6">
                                            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_50px_rgba(220,38,38,0.3)] animate-pulse">
                                                <span className="text-4xl text-white">⚠️</span>
                                            </div>
                                            <h2 className="text-3xl font-black text-white mb-3 tracking-tighter uppercase italic">LOTO Verificatie Verplicht</h2>
                                            <p className="max-w-md mx-auto text-slate-400 text-sm mb-6 leading-relaxed">
                                                U voert werkzaamheden uit met een hoog risico-profiel. <br />
                                                <span className="text-white font-bold">Het werkplan blijft vergrendeld</span> totdat de veiligheidsstelling (LOTO) is bevestigd met foto's.
                                            </p>
                                            <button
                                                onClick={() => {
                                                    setExpandedSections(prev => ({ ...prev, lmra: false, safety: true }));
                                                    // Allow state to update then scroll
                                                    setTimeout(() => {
                                                        const el = document.getElementById('loto-section-container');
                                                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                    }, 100);
                                                }}
                                                className="btn btn-lg bg-white hover:bg-slate-200 text-black border-none gap-3 px-10 shadow-xl font-black uppercase tracking-widest active:scale-95"
                                            >
                                                <span>🔒</span> Start LOTO Verificatie
                                            </button>
                                            <div className="mt-8 text-[9px] text-slate-600 uppercase tracking-[0.2em] font-black">
                                                Safety First • RaconWorks Integrity Engine
                                            </div>
                                        </div>
                                    )}

                                    {(() => {
                                        return (
                                            <>
                                                <div id="loto-section-container">
                                                    <LOTOSection
                                                        permit={permit}
                                                        checklist={checklist}
                                                        onUpdate={handleChecklistUpdate}
                                                        onLotoDeviation={handleLotoDeviation}
                                                        isReadOnly={isReadOnly}
                                                        previewMode={previewMode}
                                                        isGateMode={!lotoComplete && lotoSteps.length > 0}
                                                        isOpen={expandedSections.safety}
                                                        onToggle={() => toggleSection('safety')}
                                                    />
                                                </div>

                                                {lotoComplete ? (
                                                    <>
                                                        <MechanicChecklist
                                                            permit={permit}
                                                            checklist={checklist}
                                                            onUpdate={handleChecklistUpdate}
                                                            isReadOnly={isReadOnly}
                                                            previewMode={previewMode}
                                                            canUpload={canUpload}
                                                            isOpen={expandedSections.checks}
                                                            onToggle={() => toggleSection('checks')}
                                                            onExecutionUpload={addExecutionSessionUrl}
                                                        />

                                                        {/* FINAL GEREED TRIGGER */}
                                                        {status !== 'completed' && !showCompletionModal && (
                                                            <button
                                                                onClick={() => {
                                                                    handleStatusUpdate('completed');
                                                                    setExpandedSections(prev => ({ ...prev, checks: false, safety: false, lmra: false }));
                                                                }}
                                                                className={`w-full py-5 rounded-xl text-lg font-black uppercase tracking-widest mt-8 shadow-2xl transition-all active:scale-95 flex items-center justify-center gap-3 border-2 border-yellow-500/20 group hover:border-yellow-500/50 ${allChecksDone ? 'animate-pulse-glow' : ''}`}
                                                                style={styleSuccess}
                                                            >
                                                                <div className="p-1 px-2 rounded bg-yellow-900/40 text-black transform group-hover:scale-110 transition-transform">✓</div>
                                                                <span>Gereed Meldprocedure Starten</span>
                                                            </button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div
                                                        onClick={() => toggleSection('safety')}
                                                        className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6 md:p-10 text-center opacity-75 mt-4 cursor-pointer hover:bg-slate-800/50 transition-all"
                                                    >
                                                        <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-slate-700">
                                                            <span className="text-2xl grayscale">🔒</span>
                                                        </div>
                                                        <h3 className="font-black text-slate-400 uppercase tracking-widest text-sm mb-2">Bevestig LOTO Procedure</h3>
                                                        <p className="text-xs text-slate-500 leading-relaxed max-w-xs mx-auto">
                                                            De taak-specifieke checks worden ontgrendeld zodra alle veiligheidsmaatregelen zijn bevestigd.
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}


                                    {/* Exit Checklist (Inline Section) */}
                                    {showCompletionModal && (
                                        <div
                                            id="completion-panel-anchor"
                                            ref={completionPanelRef}
                                            style={{ scrollMarginTop: '180px' }}
                                        >
                                            <CompletionPanel
                                                permit={permit}
                                                onClose={() => setShowCompletionModal(false)}
                                                onComplete={handleFinalCompletion}
                                                canComplete={canComplete}
                                                completionChecks={completionChecks}
                                                setCompletionChecks={setCompletionChecks}
                                                instructionAnswer={instructionAnswer}
                                                setInstructionAnswer={setInstructionAnswer}
                                                instructionFeedback={instructionFeedback}
                                                setInstructionFeedback={setInstructionFeedback}
                                                completionFeedback={completionFeedback}
                                                setCompletionFeedback={setCompletionFeedback}
                                                conditionScore={conditionScore}
                                                setConditionScore={setConditionScore}
                                                isReplacementConfirmed={isReplacementConfirmed}
                                                setIsReplacementConfirmed={setIsReplacementConfirmed}
                                                canCompleteReason={canCompleteReason}
                                                // ISO 14224 Smart Props
                                                isoLibrary={isoLibrary}
                                                contextTask={permit.task}
                                                contextComponent={permit.component || permit.title || permit.description}
                                                // Session Tracking
                                                onExecutionUpload={addExecutionSessionUrl}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB 2: DETAILS (INFO) */}
                    {activeTab === 'details' && (
                        <div className="space-y-6 fade-in text-white">
                            <SummaryRenderer
                                config={activeConfig}
                                data={permit}
                                darkMode={true}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
